import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { caAPI, errText } from '../../lib/complianceAutoAPI';

const NotEligible = ({ row, onDone }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const go = async () => {
    try { const r = await caAPI.notEligible(row.obligation_id, reason); toast.success(`Marked not eligible – ${r.periods_changed} period(s) moved to the standard date`); onDone(); }
    catch (e) { toast.error(errText(e)); }
  };
  if (!open) return <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setOpen(true)} data-testid={`concession-not-eligible-${row.obligation_id}`}>Not eligible</Button>;
  return (
    <span className="flex items-center gap-1">
      <Input className="h-7 w-64 text-xs" placeholder="Reason (min 10 characters)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`concession-reason-${row.obligation_id}`} />
      <Button size="sm" className="h-7 text-xs" disabled={reason.trim().length < 10} onClick={go} data-testid={`concession-not-eligible-save-${row.obligation_id}`}>Save</Button>
    </span>
  );
};

export const ConcessionCard = ({ s, reload, canEdit }) => {
  const [rows, setRows] = useState([]);
  const [pv, setPv] = useState(null);
  const load = useCallback(() => {
    caAPI.concessionReview().then(setRows).catch(() => setRows([]));
    if (canEdit) caAPI.concessionPreview().then(setPv).catch(() => setPv(null));
  }, [canEdit]);
  useEffect(() => { load(); }, [load, s]);
  const on = s.assume_agent_concession !== false;
  const toggle = async (v) => { try { await caAPI.saveSettings({ assume_agent_concession: v }); toast.success('Concession setting saved'); reload(); } catch (e) { toast.error(errText(e)); } };
  const apply = async () => { try { const r = await caAPI.concessionApply(); toast.success(`${r.changed} period(s) recomputed`); load(); } catch (e) { toast.error(errText(e)); } };
  return (
    <div className="rounded-lg border p-4 space-y-3" data-testid="ca-concession-settings">
      <p className="font-medium">Concession settings</p>
      <label className="flex items-start gap-2">
        <Switch checked={on} disabled={!canEdit} onCheckedChange={toggle} data-testid="ca-assume-concession" />
        <span>Assume new BAS clients qualify for the agent lodgment concession
          <span className="block text-xs text-muted-foreground">Unknown eligibility uses the agent date (e.g. 25 Nov for Q1). Known disqualifiers – paper lodgment, monthly PAYG instalments, consolidated head entity, excluded forms, monthly lodgers, Q2 – still use the standard date.</span></span>
      </label>
      <label className="flex items-center gap-2 text-xs">
        <span>First IAS period for new clients</span>
        <select className="h-7 rounded border bg-background px-2" value={s.first_ias_period || 'recent'} disabled={!canEdit} data-testid="ca-first-ias-period"
          onChange={async (e) => { try { await caAPI.saveSettings({ first_ias_period: e.target.value }); toast.success('Saved'); reload(); } catch (err) { toast.error(errText(err)); } }}>
          <option value="recent">Most recent due month (default – may already be overdue)</option>
          <option value="next">Next month</option>
        </select>
      </label>
      {canEdit && pv && pv.count > 0 && (
        <div className="flex items-center gap-2 text-xs rounded bg-muted/50 p-2" data-testid="ca-concession-preview">
          <span>{pv.count} open future period(s) would move to the {on ? 'agent' : 'standard'} date under this setting.</span>
          <Button size="sm" className="h-7 text-xs" onClick={apply} data-testid="ca-concession-apply">Recompute these periods</Button>
        </div>
      )}
      <div className="space-y-1" data-testid="ca-concession-review">
        <p className="text-xs font-medium">Agent concession assumed ({rows.length}) <span className="font-normal text-muted-foreground">– info only, correct any client that doesn't qualify</span></p>
        {rows.slice(0, 50).map((r) => (
          <div key={r.obligation_id} className="flex items-center gap-2 text-xs border-b py-1" data-testid={`concession-row-${r.obligation_id}`}>
            <span className="flex-1">{r.client_name} · {r.obligation_type} <span className="text-muted-foreground">({r.detail})</span></span>
            {canEdit && <NotEligible row={r} onDone={load} />}
          </div>
        ))}
      </div>
    </div>
  );
};
